import { ApiBase } from "../core/api-base";
import type * as z from "zod";
import {
    GetNotifications200Response,
    GetUnreadCount200Response,
    MarkNotificationAsReadParams,
} from "../api/generated/notifications.zod";

type NotificationsResponse = z.infer<typeof GetNotifications200Response>;
type UnreadCountResponse = z.infer<typeof GetUnreadCount200Response>;

export type NotificationItem = NonNullable<NonNullable<NotificationsResponse["data"]>["items"]>[number];

export interface FetchNotificationsResult {
    ok: boolean;
    items: NotificationItem[];
}

class NotificationsApi extends ApiBase {
    public async fetchUnreadCount(): Promise<number> {
        const res = await super.apiGet("/api/v1/notifications/unread-count", {
            noSpinner: true,
        });

        if (!res.ok) return 0;

        const json = (await res.json()) as UnreadCountResponse;
        const count = Number(json?.data?.unread_count ?? 0);
        return Number.isFinite(count) && count > 0 ? count : 0;
    }

    public async fetchNotifications(): Promise<FetchNotificationsResult> {
        const res = await super.apiGet("/api/v1/notifications", {
            noSpinner: true,
        });

        if (!res.ok) {
            return { ok: false, items: [] };
        }

        const json = (await res.json()) as NotificationsResponse;
        const items = Array.isArray(json?.data?.items) ? json.data.items : [];
        return { ok: true, items };
    }

    public async markNotificationRead(
        notificationId: z.infer<typeof MarkNotificationAsReadParams>["notification_id"],
    ): Promise<void> {
        if (!Number.isFinite(notificationId) || notificationId <= 0) return;

        await super.apiPost(`/api/v1/notifications/${notificationId}/read`, null, {
            noSpinner: true,
        });
    }
}

const notificationsApi = new NotificationsApi();

export async function fetchUnreadCount(): Promise<number> {
    return notificationsApi.fetchUnreadCount();
}

export async function fetchNotifications(): Promise<FetchNotificationsResult> {
    return notificationsApi.fetchNotifications();
}

export async function markNotificationRead(notificationId: number): Promise<void> {
    await notificationsApi.markNotificationRead(notificationId);
}
